import React, { useEffect, useMemo, useState } from 'react';
import { ControllerRenderProps, FieldValues, useForm } from 'react-hook-form';
import { Department, Team } from '../../../../models';
import { useAppActions } from '../../../../overmind';
import { useParams } from 'react-router';
import { Button, DropdownMenu, DropdownSearch, DropdownSelect, EmailValidator, FieldController, PanelHeader, RequiredValidator, TextField } from '@keplerco/core';
import { UserBulkUploadRow } from '../../../../models/bulk-uploads/bulk-upload-dto';
import { BulkUploadFieldNameEnum, IBulkUploadEditFocusPanelProps } from './bulk-upload.models';
import { LANGUAGES } from '../../../../library/consts/languages';
import { FlagIcon } from '../../../../design/icons/flag.icon';
import { Country } from '../../../onboarding/classic/classic.onboarding.layout.models';

export default function BulkUploadEditFocusPanel({ selectedRow, onSubmit }: IBulkUploadEditFocusPanelProps) {
  const params = useParams();

  const defaultValues: FieldValues = useMemo(
    () => ({
      firstName: selectedRow?.firstName ?? '',
      lastName: selectedRow?.lastName ?? '',
      email: selectedRow?.email ?? '',
      jobTitle: selectedRow?.jobTitle ?? '',
      department: selectedRow?.department ?? '',
      team: selectedRow?.team ?? '',
      country: selectedRow?.country ?? '',
      languages: !!selectedRow?.languages ? (Array.isArray(selectedRow.languages) ? selectedRow.languages : (selectedRow.languages as string).split(',').map(language => language.trim())) : [],
    }),
    [selectedRow]
  );

  const actions = useAppActions();

  const [departments, setDepartments] = useState<Department[]>();
  const [teams, setTeams] = useState<Team[]>();
  const [countries, setCountries] = useState<Country[]>();
  const [departmentSlug, setDepartmentSlug] = useState<string>();

  const { control, handleSubmit, setValue } = useForm<FieldValues>({ mode: 'onChange', reValidateMode: 'onBlur', defaultValues });

  useEffect(() => {
    actions.getTeams(params.companySlug!).then(fetchedTeams => {
      setTeams(fetchedTeams?.sort((a, b) => a.teamName.localeCompare(b.teamName)));
    });

    actions.getDepartments(params.companySlug!).then(fetchedDepartments => {
      setDepartments(fetchedDepartments?.sort((a, b) => a.name.localeCompare(b.name)));
    });

    actions.getCountries().then(fetchedCountries => {
      setCountries(fetchedCountries);
    });
  }, [params.companySlug, selectedRow?.department, selectedRow?.team, selectedRow?.country]);

  function handleFormSubmit(fieldValues: FieldValues) {
    if (!!selectedRow) {
      const languages = typeof fieldValues.languages === 'string' ? fieldValues.languages.split(',').map((language: string) => language.trim()) : Array.isArray(fieldValues.languages) ? fieldValues.languages : [];

      const row: UserBulkUploadRow = {
        firstName: fieldValues.firstName,
        lastName: fieldValues.lastName,
        email: fieldValues.email,
        jobTitle: fieldValues.jobTitle,
        department: fieldValues.department,
        learnerSlug: fieldValues.learnerSlug,
        team: fieldValues.team,
        country: fieldValues.country,
        languages,
        rowNumber: selectedRow.rowNumber,
        isValid: selectedRow.isValid,
        validationMessages: selectedRow.validationMessages,
      };

      onSubmit(row);
    }
  }

  function getFieldError(fieldName: string): string | undefined {
    const error = selectedRow?.validationMessages?.find(err => err.fieldName === fieldName);
    return error ? error.message : undefined;
  }

  function clearApiError(fieldName: string) {
    if (selectedRow) {
      const updatedValidationMessages = selectedRow.validationMessages?.filter(err => err.fieldName !== fieldName);

      selectedRow.validationMessages = updatedValidationMessages;
    }
  }

  function onChangeHandler(field: ControllerRenderProps<FieldValues, string>, e: React.ChangeEvent<HTMLInputElement>, fieldName: string) {
    field.onChange(e);
    clearApiError(fieldName);
  }

  return (
    <div className="panelContent panelForm">
      <PanelHeader title={`Manage ${selectedRow?.firstName} ${selectedRow?.lastName}`} divider />

      <form className="panelForm" id="editBulkUpload" onSubmit={handleSubmit(handleFormSubmit)}>
        <div>
          <div className="row">
            <div className="column">
              <FieldController
                control={control}
                rules={new RequiredValidator('Enter a first name')}
                name="firstName"
                render={({ field, fieldState }) => {
                  const fieldError = getFieldError(BulkUploadFieldNameEnum.FirstName);
                  return (
                    <TextField
                      {...field}
                      type="text"
                      label="First name"
                      onChange={e => onChangeHandler(field, e, BulkUploadFieldNameEnum.FirstName)}
                      responsive
                      validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a first name' }}
                    />
                  );
                }}
              />
            </div>

            <div className="column">
              <FieldController
                control={control}
                rules={new RequiredValidator('Enter a last name')}
                name="lastName"
                render={({ field, fieldState }) => {
                  const fieldError = getFieldError(BulkUploadFieldNameEnum.LastName);
                  return (
                    <TextField
                      {...field}
                      type="text"
                      label="Last name"
                      onChange={e => onChangeHandler(field, e, BulkUploadFieldNameEnum.LastName)}
                      responsive
                      validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a last name' }}
                    />
                  );
                }}
              />
            </div>
          </div>

          <div className="row">
            <div className="column">
              <FieldController
                control={control}
                rules={new EmailValidator()}
                name="email"
                render={({ field, fieldState }) => {
                  const fieldError = getFieldError(BulkUploadFieldNameEnum.Email);
                  return (
                    <TextField
                      {...field}
                      type="email"
                      label="Email"
                      onChange={e => onChangeHandler(field, e, BulkUploadFieldNameEnum.Email)}
                      responsive
                      validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a first name' }}
                    />
                  );
                }}
              />
            </div>

            <div className="column">
              <FieldController
                control={control}
                rules={new RequiredValidator('Enter a job title')}
                name="jobTitle"
                render={({ field, fieldState }) => {
                  const fieldError = getFieldError(BulkUploadFieldNameEnum.JobTitle);
                  return (
                    <TextField
                      {...field}
                      type="text"
                      label="Job title"
                      onChange={e => onChangeHandler(field, e, BulkUploadFieldNameEnum.JobTitle)}
                      responsive
                      validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Enter a job title' }}
                    />
                  );
                }}
              />
            </div>
          </div>

          {!!countries && (
            <FieldController
              control={control}
              name="country"
              rules={new RequiredValidator('Choose a country')}
              render={({ field, fieldState }) => {
                const fieldError = getFieldError(BulkUploadFieldNameEnum.Country);
                return (
                  <DropdownSearch
                    {...field}
                    label="Country"
                    options={countries.map(country => ({
                      value: country.name,
                      icon: <FlagIcon isoCode={country.iso} />,
                      onClick: () => {
                        clearApiError(BulkUploadFieldNameEnum.Country);
                      },
                    }))}
                    validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Please select a country' }}
                    responsive
                    dialogContained
                  />
                );
              }}
            />
          )}

          {!!departments && (
            <FieldController
              control={control}
              name="department"
              rules={new RequiredValidator('Choose a department')}
              render={({ field, fieldState }) => {
                const fieldError = getFieldError(BulkUploadFieldNameEnum.Department);
                return (
                  <DropdownSelect
                    {...field}
                    label="Department"
                    options={departments.map(department => ({
                      value: department.name,
                      onClick: () => {
                        setDepartmentSlug(department.slug);
                        setValue('team', undefined);
                        clearApiError(BulkUploadFieldNameEnum.Department);
                      },
                    }))}
                    validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Choose a department' }}
                    responsive
                  />
                );
              }}
            />
          )}

          {!!teams && (
            <FieldController
              control={control}
              name="team"
              rules={new RequiredValidator('Choose a team')}
              render={({ field, fieldState }) => {
                const fieldError = getFieldError(BulkUploadFieldNameEnum.Team);
                return (
                  <DropdownSelect
                    {...field}
                    label="Team"
                    options={teams
                      .filter(team => (!departmentSlug ? true : team.departmentSlug === departmentSlug))
                      .map(team => ({
                        value: team.teamName,
                        onClick: () => {
                          clearApiError(BulkUploadFieldNameEnum.Team);
                        },
                      }))}
                    validation={{ dirty: !!fieldError || fieldState.isDirty, invalid: !!fieldError || !!fieldState.error, message: fieldError ?? fieldState.error?.message ?? 'Choose a team' }}
                    responsive
                  />
                );
              }}
            />
          )}

          <FieldController
            control={control}
            name="languages"
            render={({ field }) => {
              return (
                <DropdownMenu
                  {...field}
                  multiple
                  label="Languages"
                  options={LANGUAGES.map(language => ({
                    value: language,
                  }))}
                  responsive
                />
              );
            }}
          />
        </div>

        <footer className="panelFooter" style={{ justifyContent: 'end' }}>
          <Button type="button" filled>
            Update
          </Button>
        </footer>
      </form>
    </div>
  );
}
